import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Phone, Check, RotateCcw, Trash2, Inbox, BellRing, BellOff, Mail, Camera, Send, FileSpreadsheet, Receipt , Truck, Star, Wrench, User } from "lucide-react";
import { API, BACKEND_URL, auth, ghostBtn, primaryBtn, inputClass, labelClass, fmtDate, handleAuthError } from "./shared";
import { useConfirm } from "./useConfirm";
import EstimateDialog from "./EstimateDialog";

const REPAIR_STEPS = ["recu", "en_cours", "pret", "livre"];
const REPAIR_LABELS = { recu: "Reçu", en_cours: "En cours", pret: "Prêt", livre: "Livré" };

const waLink = (q) => {
  const digits = q.phone.replace(/\D/g, "");
  const text = `Bonjour ${q.name}, GROUPE AUTO LUXE a bien reçu votre demande de devis (${q.service}${q.vehicle ? ` — ${q.vehicle}` : ""}). `;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
};

const fmt = (v) => `${Math.round(v || 0).toLocaleString("fr-FR")} FCFA`;
const PAY_LABELS = { cash: "Espèces", wave: "Wave", mtn: "MTN Mobile Money", orange: "Orange Money", transfer: "Virement bancaire", card: "Carte bancaire", other: "Autre" };

const AdminQuotes = ({ token, logout }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [notif, setNotif] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ method: "cash", amount: "", reference: "" });

  const openPay = (q) => { setPayFor(q.id); setPayForm({ method: "cash", amount: String(q.invoice.total), reference: "" }); };
  const confirmPaid = async (q) => {
    try {
      const r = await axios.post(`${API}/quotes/${q.id}/invoice/paid`, { method: payForm.method, amount: payForm.amount === "" ? null : Number(payForm.amount), reference: payForm.reference }, auth(token));
      if (r.data.whatsapp_url) window.open(r.data.whatsapp_url, "_blank", "noopener");
      toast.success(`Paiement enregistré${r.data.emailed ? ` · reçu envoyé à ${q.email}` : ""}${r.data.whatsapp_url ? " · reçu WhatsApp préparé" : ""}`);
      setPayFor(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Enregistrement impossible"));
    }
  };
  const clientLink = async (q) => {
    try {
      const r = await axios.get(`${API}/quotes/${q.id}/client-link`, auth(token));
      window.open(r.data.whatsapp_url, "_blank", "noopener");
      toast.success(`Lien de la fiche client préparé dans WhatsApp pour ${q.phone}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Lien indisponible"));
    }
  };
  const remindUnpaid = async (q) => {
    try {
      const r = await axios.post(`${API}/quotes/${q.id}/invoice/remind`, {}, auth(token));
      if (r.data.whatsapp_url) window.open(r.data.whatsapp_url, "_blank", "noopener");
      toast.success(`Relance ${r.data.emailed ? `envoyée à ${q.email}` : "préparée"}${r.data.whatsapp_url ? " · WhatsApp ouvert" : ""}`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Relance impossible"));
    }
  };
  const cancelPaid = async (q) => {
    if (!(await confirm({ title: "Annuler le paiement ?", description: `La facture ${q.invoice.number} redeviendra « à régler ».` }))) return;
    try { await axios.delete(`${API}/quotes/${q.id}/invoice/paid`, auth(token)); toast.success("Paiement annulé"); load(); }
    catch (err) { handleAuthError(err, logout, () => toast.error("Annulation impossible")); }
  };
  const { confirm, dialog } = useConfirm();

  const load = () =>
    axios.get(`${API}/quotes`, auth(token)).then((r) => setItems(r.data)).catch((err) => handleAuthError(err, logout, () => {}));
  useEffect(() => {
    load();
    axios.get(`${API}/notifications/status`, auth(token)).then((r) => setNotif(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}`, { status }, auth(token));
      toast.success(status === "handled" ? "Demande marquée traitée" : "Demande réouverte");
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de la mise à jour"));
    }
  };

  const remove = async (q) => {
    const ok = await confirm({ title: "Supprimer cette demande ?", description: `La demande de ${q.name} (${q.service}) sera définitivement supprimée.` });
    if (!ok) return;
    try {
      await axios.delete(`${API}/quotes/${q.id}`, auth(token));
      toast.success("Demande supprimée");
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de la suppression"));
    }
  };

  const remind = async (q) => {
    try {
      const r = await axios.post(`${API}/quotes/${q.id}/remind`, {}, auth(token));
      const parts = [r.data.email && "email", r.data.whatsapp && "WhatsApp"].filter(Boolean);
      if (parts.length) toast.success(`Rappel envoyé par ${parts.join(" et ")}`);
      else toast.error("Aucun canal disponible (email client manquant / Twilio non configuré)");
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error(err.response?.data?.detail || "Échec de l'envoi du rappel"));
    }
  };

  const exportExcel = async () => {
    try {
      const res = await axios.get(`${API}/quotes/export`, { ...auth(token), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `devis-groupe-auto-luxe-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Fichier Excel téléchargé");
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de l'export"));
    }
  };

  const inviteReview = async (q) => {
    try { await axios.post(`${API}/quotes/${q.id}/review-invite`, {}, auth(token)); toast.success(`Invitation à laisser un avis envoyée à ${q.email}`); load(); }
    catch (err) { handleAuthError(err, logout, () => toast.error(err.response?.data?.detail || "Échec de l'envoi")); }
  };

  const setRepair = async (q, repair_status) => {
    try {
      const r = await axios.patch(`${API}/quotes/${q.id}/repair`, { repair_status }, auth(token));
      if (r.data.emailed) toast.success(`Suivi mis à jour : ${REPAIR_LABELS[repair_status]}. Client prévenu par e-mail.`);
      else if (r.data.whatsapp_url) toast.success(`Suivi mis à jour : ${REPAIR_LABELS[repair_status]}`, { description: "Ce client n'a pas d'e-mail : prévenez-le sur WhatsApp.", action: { label: "Prévenir sur WhatsApp", onClick: () => window.open(r.data.whatsapp_url, "_blank", "noopener") }, duration: 10000 });
      else toast.success(`Suivi mis à jour : ${REPAIR_LABELS[repair_status]}`);
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de la mise à jour du suivi"));
    }
  };

  const makeInvoice = async (q) => {
    try {
      const r = await axios.post(`${API}/quotes/${q.id}/invoice`, {}, auth(token));
      toast.success(`Facture ${r.data.number} générée`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Échec de la facturation"));
    }
  };

  const sendInvoice = async (q) => {
    try {
      await axios.post(`${API}/quotes/${q.id}/invoice/send`, {}, auth(token));
      toast.success(`Facture envoyée à ${q.email}`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Échec de l'envoi"));
    }
  };

  const whatsappMessage = async (q, kind, label) => {
    try {
      const r = await axios.get(`${API}/quotes/${q.id}/whatsapp-message?kind=${kind}`, auth(token));
      window.open(r.data.url, "_blank", "noopener");
      toast.success(`${label} préparé dans WhatsApp pour ${q.phone}`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Échec de la préparation"));
    }
  };

  const maintenanceRemind = async (q) => {
    try {
      const r = await axios.post(`${API}/quotes/${q.id}/maintenance-remind`, {}, auth(token));
      if (r.data.whatsapp_url) { window.open(r.data.whatsapp_url, "_blank", "noopener"); toast.success(`Rappel d'entretien préparé dans WhatsApp pour ${q.phone}`); }
      else toast.success(`Rappel d'entretien envoyé par e-mail à ${q.email}`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Échec de l'envoi"));
    }
  };

  const addAfterPhotos = async (q, files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try {
      await axios.post(`${API}/quotes/${q.id}/after-photos`, fd, auth(token));
      toast.success("Photos du véhicule réparé ajoutées");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Échec de l'envoi des photos"));
    }
  };

  const removeAfterPhoto = async (q, path) => {
    try {
      await axios.delete(`${API}/quotes/${q.id}/after-photos`, { ...auth(token), params: { path } });
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de la suppression"));
    }
  };

  const sendWhatsappDoc = async (q, kind) => {
    try {
      const r = await axios.get(`${API}/quotes/${q.id}/document/whatsapp?kind=${kind}`, auth(token));
      window.open(r.data.url, "_blank", "noopener");
      toast.success(`${kind === "invoice" ? "Facture" : "Devis"} préparé dans WhatsApp pour ${q.phone}`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Échec de la préparation"));
    }
  };

  const visible = filter === "all" ? items : items.filter((q) => q.status === filter);
  const newCount = items.filter((q) => q.status === "new").length;

  return (
    <div data-testid="admin-quotes-tab">
      {dialog}
      {notif && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="admin-notif-status">
          <div className={`flex items-start gap-3 rounded-2xl p-5 ${notif.twilio_whatsapp ? "bg-mint" : "bg-cream"}`} data-testid="admin-notif-whatsapp">
            {notif.twilio_whatsapp ? <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-brand" /> : <BellOff className="mt-0.5 h-5 w-5 shrink-0 text-gold" />}
            <div className="text-sm text-forest">
              <b>WhatsApp</b> — {notif.twilio_whatsapp ? "alerte automatique activée" : "alerte automatique non configurée (clés Twilio manquantes)"}.
              <br />Destinataires : {notif.alert_numbers.join(", ")}
            </div>
          </div>
          <div className={`flex items-start gap-3 rounded-2xl p-5 ${notif.email ? "bg-mint" : "bg-cream"}`} data-testid="admin-notif-email">
            <Mail className={`mt-0.5 h-5 w-5 shrink-0 ${notif.email ? "text-brand" : "text-gold"}`} />
            <div className="text-sm text-forest">
              <b>E-mail</b> — {notif.email ? <>nouvelles demandes et questions envoyées à <b>{notif.admin_email}</b>, rappels de rendez-vous activés</> : "non configuré"}. WhatsApp {notif.twilio_whatsapp ? "activé" : "non configuré"}.
              <br />Rappels envoyés automatiquement la veille à {notif.reminder_hour}h00 aux clients ayant un rendez-vous.
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3">
        {[
          { id: "all", label: `Toutes (${items.length})` },
          { id: "new", label: `Nouvelles (${newCount})` },
          { id: "handled", label: `Traitées (${items.length - newCount})` },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`admin-quotes-filter-${f.id}`}
            className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-all ${
              filter === f.id ? "border-gold bg-gold text-white" : "border-brand/20 bg-white text-brand hover:bg-mint"
            }`}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={exportExcel}
          data-testid="admin-quotes-export-button"
          className="ml-auto inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white shadow-lg shadow-brand/20 transition-all hover:-translate-y-0.5 hover:bg-brand-hover"
        >
          <FileSpreadsheet className="h-4 w-4" />
          Exporter Excel
        </button>
      </div>

      <div className="mt-6 space-y-4">
        {visible.map((q, i) => (
          <article
            key={q.id}
            data-testid={`admin-quote-${i}`}
            className={`rounded-2xl border p-6 transition-colors ${q.status === "new" ? "border-gold/40 bg-white shadow-md shadow-gold/5" : "border-brand/10 bg-mint/40"}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white ${q.status === "new" ? "bg-gold" : "bg-brand"}`} data-testid={`admin-quote-${i}-status`}>
                    {q.status === "new" ? "Nouvelle" : "Traitée"}
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-widest text-brand">{fmtDate(q.created_at)}</span>
                  {q.tracking_code && <span className="rounded-full bg-mint px-2.5 py-1 font-mono text-[10px] tracking-widest text-brand" data-testid={`admin-quote-${i}-tracking`}>{q.tracking_code}</span>}
                  {q.request_type === "pickup" && <span className="inline-flex items-center gap-1 rounded-full bg-forest px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white" data-testid={`admin-quote-${i}-pickup`}><Truck className="h-3 w-3" /> Enlèvement</span>}
                  {q.request_type === "devis" && <span className="inline-flex items-center gap-1 rounded-full bg-gold px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white" data-testid={`admin-quote-${i}-devis`}>Devis</span>}
                  {(!q.request_type || q.request_type === "rdv") && <span className="inline-flex items-center gap-1 rounded-full bg-mint px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-brand" data-testid={`admin-quote-${i}-rdv`}>Rendez-vous</span>}
                </div>
                <h3 className="font-display mt-3 text-xl font-bold text-forest" data-testid={`admin-quote-${i}-name`}>{q.name}</h3>
                <a href={`tel:${q.phone.replace(/\s/g, "")}`} className="mt-1 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-gold" data-testid={`admin-quote-${i}-phone`}>
                  <Phone className="h-3.5 w-3.5" /> {q.phone}
                </a>
                {q.email && (
                  <a href={`mailto:${q.email}`} className="mt-1 flex items-center gap-2 text-sm text-brand hover:text-gold" data-testid={`admin-quote-${i}-email`}>
                    <Mail className="h-3.5 w-3.5" /> {q.email}
                  </a>
                )}
              </div>
              <div className="grid gap-1 text-sm text-forest sm:text-right">
                <p><span className="font-mono text-[10px] uppercase tracking-widest text-brand">Service</span><br /><b data-testid={`admin-quote-${i}-service`}>{q.service}</b></p>
                {q.vehicle && <p><span className="font-mono text-[10px] uppercase tracking-widest text-brand">Véhicule</span><br />{q.vehicle}</p>}
                {q.date && <p><span className="font-mono text-[10px] uppercase tracking-widest text-brand">{q.request_type === "pickup" ? "Enlèvement souhaité" : "Rendez-vous"}</span><br /><b data-testid={`admin-quote-${i}-slot`}>{q.date}{q.time ? ` à ${q.time}` : q.pickup_period ? ` (${q.pickup_period === "morning" ? "matin" : "après-midi"})` : ""}</b></p>}
                {q.pickup_address && <p><span className="font-mono text-[10px] uppercase tracking-widest text-brand">Adresse d'enlèvement</span><br /><span data-testid={`admin-quote-${i}-pickup-address`}>{q.pickup_address}</span></p>}
              </div>
            </div>
            {q.message && <p className="mt-5 rounded-xl bg-white/70 p-4 text-sm leading-relaxed text-forest">{q.message}</p>}
            {q.photos?.length > 0 && (
              <div className="mt-4" data-testid={`admin-quote-${i}-photos`}>
                <p className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-brand"><Camera className="h-3 w-3" /> Photos des dégâts ({q.photos.length})</p>
                <div className="flex flex-wrap gap-2">
                  {q.photos.map((p, j) => (
                    <a key={p} href={`${BACKEND_URL}/api/quotes/photo/${p}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-quote-${i}-photo-${j}`} className="block h-20 w-20 overflow-hidden rounded-xl border border-brand/10 bg-mint transition-transform hover:scale-105">
                      <img src={`${BACKEND_URL}/api/quotes/photo/${p}`} alt="" className="h-full w-full object-cover" loading="lazy" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            {(q.repair_status === "livre" || q.repair_status === "pret" || q.after_photos?.length > 0) && (
              <div className="mt-4" data-testid={`admin-quote-${i}-after-photos`}>
                <p className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-brand"><Camera className="h-3 w-3" /> Photos du véhicule réparé ({q.after_photos?.length || 0}) · jointes à la facture (e-mail et WhatsApp)</p>
                <div className="flex flex-wrap items-center gap-2">
                  {(q.after_photos || []).map((p, j) => (
                    <div key={p} className="group relative h-20 w-20 overflow-hidden rounded-xl border border-brand/10 bg-mint" data-testid={`admin-quote-${i}-after-photo-${j}`}>
                      <a href={`${BACKEND_URL}/api/quotes/photo/${p}`} target="_blank" rel="noopener noreferrer"><img src={`${BACKEND_URL}/api/quotes/photo/${p}`} alt="" className="h-full w-full object-cover" loading="lazy" /></a>
                      <button type="button" onClick={() => removeAfterPhoto(q, p)} data-testid={`admin-quote-${i}-after-photo-${j}-remove`} aria-label="Retirer" className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-red-600 opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  ))}
                  {(q.after_photos?.length || 0) < 8 && (
                    <label data-testid={`admin-quote-${i}-after-photos-add`} className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-brand/30 text-brand transition-colors hover:border-gold hover:text-gold">
                      <Camera className="h-5 w-5" /><span className="mt-1 font-mono text-[9px] uppercase">Ajouter</span>
                      <input type="file" accept="image/*" multiple className="hidden" data-testid={`admin-quote-${i}-after-photos-input`} onChange={(e) => { addAfterPhotos(q, e.target.files); e.target.value = ""; }} />
                    </label>
                  )}
                </div>
              </div>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-2 rounded-xl bg-white/70 p-3" data-testid={`admin-quote-${i}-repair`}>
              <span className="mr-1 font-mono text-[10px] uppercase tracking-widest text-brand">Suivi véhicule{q.email ? " (client prévenu par email)" : ""} :</span>
              {REPAIR_STEPS.map((s) => {
                const active = q.repair_status === s;
                const passed = q.repair_status && REPAIR_STEPS.indexOf(q.repair_status) > REPAIR_STEPS.indexOf(s);
                return (
                  <button
                    key={s}
                    onClick={() => setRepair(q, s)}
                    data-testid={`admin-quote-${i}-repair-${s}`}
                    className={`rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-all ${
                      active ? "bg-gold text-white shadow-md shadow-gold/25" : passed ? "bg-brand text-white" : "border border-brand/20 bg-white text-brand hover:bg-mint"
                    }`}
                  >
                    {REPAIR_LABELS[s]}
                  </button>
                );
              })}
              {q.repair_status && q.phone && (
                <button onClick={() => whatsappMessage(q, "status", "Statut")} data-testid={`admin-quote-${i}-status-whatsapp`} className={`${ghostBtn} px-3 py-1.5`} title="Prévenir le client du statut actuel sur WhatsApp">
                  <MessageCircle className="h-3 w-3" /> Statut WhatsApp
                </button>
              )}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <a href={waLink(q)} target="_blank" rel="noopener noreferrer" data-testid={`admin-quote-${i}-whatsapp`} className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white transition-colors hover:bg-brand-hover">
                <MessageCircle className="h-3.5 w-3.5" /> Répondre sur WhatsApp
              </a>
              <EstimateDialog key={`${q.id}-${q.estimate?.created_at || ""}`} quote={q} token={token} logout={logout} onSaved={load} />
              {q.estimate?.decision && (
                <span data-testid={`admin-quote-${i}-decision`} className={`inline-flex items-center rounded-full px-3 py-2 font-mono text-[10px] uppercase tracking-widest ${q.estimate.decision === "accepted" ? "bg-mint text-brand" : "bg-cream text-gold"}`}>
                  {q.estimate.decision === "accepted" ? "✓ Devis accepté par le client" : "✗ Devis refusé par le client"}
                </span>
              )}
              {q.repair_status === "livre" && q.email && (
                <button onClick={() => inviteReview(q)} data-testid={`admin-quote-${i}-review-invite`} className={`${ghostBtn} ${q.review_invite_sent ? "opacity-60" : "border-gold text-gold"}`} title={q.review_invite_sent ? "Invitation déjà envoyée (renvoyer)" : "Inviter le client à laisser un avis"}>
                  <Star className="h-3.5 w-3.5" /> {q.review_invite_sent ? "Avis demandé" : "Demander un avis"}
                </button>
              )}
              {q.repair_status === "livre" && q.phone && (
                <button onClick={() => whatsappMessage(q, "review", "Invitation à laisser un avis")} data-testid={`admin-quote-${i}-review-whatsapp`} className={ghostBtn} title="Inviter le client à laisser un avis via WhatsApp">
                  <MessageCircle className="h-3.5 w-3.5" /> Avis WhatsApp
                </button>
              )}
              {q.estimate && !q.invoice && (
                <button onClick={() => makeInvoice(q)} data-testid={`admin-quote-${i}-invoice`} className={ghostBtn} title={q.repair_status === "livre" ? "Générer la facture" : "Disponible quand le véhicule est marqué Livré"}>
                  <Receipt className="h-3.5 w-3.5" /> Générer la facture
                </button>
              )}
              {q.invoice && (
                <>
                  <a href={`${BACKEND_URL}/api/quotes/${q.id}/invoice.pdf?key=${q.estimate.pdf_key}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-quote-${i}-invoice-pdf`} className={`${ghostBtn} border-gold text-gold`}>
                    <Receipt className="h-3.5 w-3.5" /> Facture {q.invoice.number}
                  </a>
                  {q.email && (
                    <button onClick={() => sendInvoice(q)} data-testid={`admin-quote-${i}-invoice-send`} className={ghostBtn}>
                      <Send className="h-3.5 w-3.5" /> {q.invoice.sent_at ? "Facture envoyée ✓ (renvoyer)" : "Envoyer la facture"}
                    </button>
                  )}
                  {q.phone && (
                    <button onClick={() => sendWhatsappDoc(q, "invoice")} data-testid={`admin-quote-${i}-invoice-whatsapp`} className={ghostBtn} title="Ouvre WhatsApp avec le détail complet de la facture prérempli">
                      <MessageCircle className="h-3.5 w-3.5" /> {q.invoice.whatsapp_at ? "Facture WhatsApp ✓ (renvoyer)" : "Facture par WhatsApp"}
                    </button>
                  )}
                  {q.invoice.paid_at ? (
                    <>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-mint px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-forest" data-testid={`admin-quote-${i}-paid-badge`}><Check className="h-3 w-3 text-brand" /> Payée le {fmtDate(q.invoice.paid_at)} · {PAY_LABELS[q.invoice.paid_method] || q.invoice.paid_method} · {fmt(q.invoice.paid_amount ?? q.invoice.total)}</span>
                      {q.phone && <button onClick={() => whatsappMessage(q, "receipt", "Reçu de paiement")} data-testid={`admin-quote-${i}-receipt-whatsapp`} className={`${ghostBtn} px-3 py-1.5`}><MessageCircle className="h-3 w-3" /> Reçu WhatsApp</button>}
                      <button onClick={() => cancelPaid(q)} data-testid={`admin-quote-${i}-paid-cancel`} className={`${ghostBtn} px-3 py-1.5 opacity-70 hover:border-red-300 hover:text-red-600`}><RotateCcw className="h-3 w-3" /> Annuler</button>
                    </>
                  ) : payFor === q.id ? (
                    <div className="flex w-full flex-wrap items-end gap-2 rounded-2xl border border-gold/30 bg-cream p-3" data-testid={`admin-quote-${i}-pay-form`}>
                      <div><label className={labelClass}>Moyen</label>
                        <select value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} data-testid={`admin-quote-${i}-pay-method`} className={inputClass}>
                          {Object.entries(PAY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select></div>
                      <div><label className={labelClass}>Montant reçu (FCFA)</label><input type="number" min="0" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid={`admin-quote-${i}-pay-amount`} className={inputClass} /></div>
                      <div><label className={labelClass}>Référence (optionnel)</label><input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} data-testid={`admin-quote-${i}-pay-reference`} className={inputClass} placeholder="N° transaction" /></div>
                      <button onClick={() => confirmPaid(q)} data-testid={`admin-quote-${i}-pay-confirm`} className={primaryBtn}><Check className="h-4 w-4" /> Confirmer et envoyer le reçu</button>
                      <button onClick={() => setPayFor(null)} data-testid={`admin-quote-${i}-pay-cancel`} className={ghostBtn}>Annuler</button>
                    </div>
                  ) : (
                    <>
                      <button onClick={() => openPay(q)} data-testid={`admin-quote-${i}-paid`} className={`${ghostBtn} border-brand text-brand`} title="Marquer la facture payée et envoyer le reçu au client (e-mail + WhatsApp)">
                        <Check className="h-3.5 w-3.5" /> Paiement reçu
                      </button>
                      {(q.email || q.phone) && (
                        <button onClick={() => remindUnpaid(q)} data-testid={`admin-quote-${i}-unpaid-remind`} className={`${ghostBtn} ${q.unpaid_due ? "border-gold text-gold" : ""}`} title="Rappeler au client que la facture reste à régler (e-mail + WhatsApp)">
                          <BellRing className="h-3.5 w-3.5" /> {q.unpaid_due ? "Relance impayé à envoyer" : q.invoice.unpaid_reminders?.length ? `Relancé ${q.invoice.unpaid_reminders.length}× (relancer)` : "Relance impayé"}
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
              {q.estimate && q.phone && (
                <button onClick={() => sendWhatsappDoc(q, "estimate")} data-testid={`admin-quote-${i}-estimate-whatsapp`} className={ghostBtn} title="Ouvre WhatsApp avec le détail complet du devis prérempli">
                  <MessageCircle className="h-3.5 w-3.5" /> {q.estimate.whatsapp_at ? "Devis WhatsApp ✓ (renvoyer)" : "Devis par WhatsApp"}
                </button>
              )}
              {q.repair_status === "livre" && (q.email || q.phone) && (
                <button onClick={() => maintenanceRemind(q)} data-testid={`admin-quote-${i}-maintenance`} className={`${ghostBtn} ${q.maintenance_due ? "border-gold text-gold" : q.maintenance_reminder_sent ? "opacity-60" : ""}`} title={q.maintenance_reminder_sent ? `Rappel envoyé le ${fmtDate(q.maintenance_reminder_at)} (renvoyer)` : q.email ? "Envoyer le rappel d'entretien par e-mail" : "Préparer le rappel d'entretien sur WhatsApp"}>
                  <Wrench className="h-3.5 w-3.5" /> {q.maintenance_reminder_sent ? "Entretien rappelé ✓" : q.maintenance_due ? "Rappel entretien à envoyer" : "Rappel entretien"}
                </button>
              )}
              {q.phone && (
                <button onClick={() => clientLink(q)} data-testid={`admin-quote-${i}-client-link`} className={ghostBtn} title="Envoyer au client le lien de son espace personnel (historique, devis, factures, rappels)">
                  <User className="h-3.5 w-3.5" /> Fiche client
                </button>
              )}
              {q.repair_status === "livre" && q.phone && q.email && (
                <button onClick={() => whatsappMessage(q, "maintenance", "Rappel d'entretien")} data-testid={`admin-quote-${i}-maintenance-whatsapp`} className={ghostBtn} title="Rappel d'entretien via WhatsApp">
                  <MessageCircle className="h-3.5 w-3.5" /> Entretien WhatsApp
                </button>
              )}
              {q.date && (
                <button onClick={() => remind(q)} data-testid={`admin-quote-${i}-remind`} className={ghostBtn} title={q.reminder_sent ? "Rappel déjà envoyé — renvoyer" : "Envoyer le rappel maintenant"}>
                  <Send className="h-3.5 w-3.5" /> {q.reminder_sent ? "Rappel envoyé ✓ (renvoyer)" : "Envoyer le rappel"}
                </button>
              )}
              {q.date && q.phone && (
                <button onClick={() => whatsappMessage(q, "reminder", "Rappel de rendez-vous")} data-testid={`admin-quote-${i}-remind-whatsapp`} className={ghostBtn} title="Rappeler le rendez-vous via WhatsApp">
                  <MessageCircle className="h-3.5 w-3.5" /> Rappel WhatsApp
                </button>
              )}
              {q.status === "new" ? (
                <button onClick={() => setStatus(q.id, "handled")} data-testid={`admin-quote-${i}-handle`} className={ghostBtn}>
                  <Check className="h-3.5 w-3.5" /> Marquer traitée
                </button>
              ) : (
                <button onClick={() => setStatus(q.id, "new")} data-testid={`admin-quote-${i}-reopen`} className={ghostBtn}>
                  <RotateCcw className="h-3.5 w-3.5" /> Réouvrir
                </button>
              )}
              <button onClick={() => remove(q)} data-testid={`admin-quote-${i}-delete`} className={`${ghostBtn} hover:border-gold hover:text-gold`}>
                <Trash2 className="h-3.5 w-3.5" /> Supprimer
              </button>
            </div>
          </article>
        ))}
        {visible.length === 0 && (
          <div className="flex items-center gap-3 rounded-2xl border-2 border-dashed border-brand/20 px-6 py-10 text-brand" data-testid="admin-quotes-empty">
            <Inbox className="h-5 w-5" /> Aucune demande dans cette catégorie.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminQuotes;
